import React, { useState, useEffect } from 'react';
import {
  X,
  Send,
  Check,
  ChevronRight,
  ChevronLeft,
  Eye,
  FileCheck,
  Smartphone,
  Monitor,
  Users,
  ShieldAlert,
  AlertTriangle,
  MailCheck,
  Layers,
  Sparkles,
} from 'lucide-react';
import Swal from 'sweetalert2';
import TiptapEditor from './TiptapEditor';
import {
  createCampaign,
  updateCampaign,
  updateCampaignRecipients,
  getCampaignPreview,
  sendTestBat,
  sendCampaign,
  getArticles,
  getSubscribers,
} from '../../../services/newsNewsletterApi';

const TEMPLATES = [
  {
    key: 'STANDARD_DIGEST',
    name: 'Standard Digest (Digest Solaire)',
    desc: 'Format récapitulatif idéal pour les actualités sectorielles, nouveautés réglementaires et veille de marché.',
  },
  {
    key: 'REGULATORY_ALERT',
    name: 'Alerte Réglementaire (JORT / STEG)',
    desc: 'Format prioritaire pour les décrets urgents, nouvelles lois de finances et évolutions tarifaires.',
  },
  {
    key: 'SINGLE_FEATURE',
    name: 'Focus Spécial (Grand Dossier)',
    desc: 'Mise en avant d’une actualité unique majeure ou d’une réalisation de projet solaire d’envergure.',
  },
  {
    key: 'CUSTOM_HTML',
    name: 'HTML Personnalisé',
    desc: 'Liberté complète pour une mise en page sur-mesure respectant les normes de livrabilité RFC 8058.',
  },
];

export default function CampaignWizardModal({
  isOpen,
  onClose,
  campaign = null,
  onCampaignUpdated,
}) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form State
  const [internalName, setInternalName] = useState('');
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [templateKey, setTemplateKey] = useState('STANDARD_DIGEST');
  const [editorialIntroHtml, setEditorialIntroHtml] = useState('');
  const [senderName, setSenderName] = useState('SASTRA Energy');
  const [senderEmail, setSenderEmail] = useState('newsletter@sastraenergy.com');

  // Featured Articles
  const [availableArticles, setAvailableArticles] = useState([]);
  const [selectedArticleIds, setSelectedArticleIds] = useState([]);

  // Recipients
  const [selectionMode, setSelectionMode] = useState('ALL_ELIGIBLE'); // 'ALL_ELIGIBLE' | 'MANUAL_SELECTION'
  const [subscribers, setSubscribers] = useState([]);
  const [selectedSubscriberIds, setSelectedSubscriberIds] = useState([]);
  const [subscribersSearch, setSubscribersSearch] = useState('');
  const [subscribersPage, setSubscribersPage] = useState(1);
  const [subscribersTotal, setSubscribersTotal] = useState(0);
  const [eligibleCount, setEligibleCount] = useState(0);
  const [rejectedSubscribers, setRejectedSubscribers] = useState([]);

  // Preview & BAT
  const [previewData, setPreviewData] = useState(null);
  const [previewDevice, setPreviewDevice] = useState('desktop'); // 'desktop' | 'mobile' | 'text'
  const [batEmails, setBatEmails] = useState('');

  // Send Confirmation Lock
  const [confirmationTyped, setConfirmationTyped] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState('');

  const [activeCampaignId, setActiveCampaignId] = useState(null);

  const currentUserType = localStorage.getItem('typeUser');
  const isAdmin = currentUserType === 'admin';

  useEffect(() => {
    if (isOpen) {
      loadArticles();
      loadSubscribers();
      if (campaign) {
        setActiveCampaignId(campaign._id);
        setInternalName(campaign.internalName || '');
        setSubject(campaign.subject || '');
        setPreheader(campaign.preheader || '');
        setTemplateKey(campaign.templateKey || 'STANDARD_DIGEST');
        setEditorialIntroHtml(campaign.editorialIntroHtml || '');
        setSelectedArticleIds((campaign.featuredArticles || []).map((a) => a.articleId?._id || a.articleId));
        setSelectionMode(campaign.recipientSelectionMode || 'ALL_ELIGIBLE');
        setSelectedSubscriberIds(campaign.selectedSubscriberIds || []);
      } else {
        // Reset
        setActiveCampaignId(null);
        setInternalName('');
        setSubject('');
        setPreheader('');
        setTemplateKey('STANDARD_DIGEST');
        setEditorialIntroHtml('');
        setSelectedArticleIds([]);
        setSelectionMode('ALL_ELIGIBLE');
        setSelectedSubscriberIds([]);
      }
      setStep(1);
      setConfirmationTyped('');
      setIdempotencyKey(`idemp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`);
    }
  }, [isOpen, campaign]);

  const loadArticles = async () => {
    try {
      const res = await getArticles({ limit: 50, validationStatus: 'APPROVED' });
      if (res?.success) {
        setAvailableArticles(res.data || []);
      }
    } catch (err) {
      console.error('Erreur chargement articles:', err);
    }
  };

  const loadSubscribers = async (search = '', page = 1) => {
    try {
      const res = await getSubscribers({ search, page, limit: 10, isExcluded: false });
      if (res?.success) {
        setSubscribers(res.data || []);
        setSubscribersTotal(res.pagination?.total || 0);
        setSubscribersPage(page);
      }
    } catch (err) {
      console.error('Erreur chargement abonnés:', err);
    }
  };

  const toggleSubscriberSelection = (id) => {
    setSelectedSubscriberIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleArticleSelection = (id) => {
    setSelectedArticleIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // --------------------------------------------------------------------------
  // Step Navigation & API Sync
  // --------------------------------------------------------------------------

  const handleNextStep = async () => {
    if (step === 1) {
      if (!internalName.trim() || internalName.length < 3) {
        Swal.fire('Validation', 'Le nom interne doit comporter au moins 3 caractères.', 'warning');
        return;
      }
      if (!subject.trim() || subject.length < 5) {
        Swal.fire('Validation', 'L’objet de l’email doit comporter au moins 5 caractères.', 'warning');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      setStep(3);
    } else if (step === 3) {
      // Save draft campaign before proceeding
      setIsSubmitting(true);
      try {
        const payload = {
          internalName,
          subject,
          preheader,
          templateKey,
          editorialIntroHtml,
          sender: { fromName: senderName, fromEmail: senderEmail },
          featuredArticles: selectedArticleIds.map((id, index) => ({
            articleId: id,
            displayOrder: index,
          })),
          recipientSelectionMode: selectionMode,
        };

        let currentId = activeCampaignId;
        if (currentId) {
          await updateCampaign(currentId, payload);
        } else {
          const res = await createCampaign(payload);
          currentId = res.data._id;
          setActiveCampaignId(currentId);
        }
        setStep(4);
      } catch (err) {
        Swal.fire('Erreur', err.message || 'Impossible de sauvegarder la campagne', 'error');
        return;
      } finally {
        setIsSubmitting(false);
      }
    } else if (step === 4) {
      // Validate recipients selection on backend
      setIsSubmitting(true);
      try {
        const payload = {
          recipientSelectionMode: selectionMode,
          selectedSubscriberIds: selectionMode === 'MANUAL_SELECTION' ? selectedSubscriberIds : [],
        };
        const res = await updateCampaignRecipients(activeCampaignId, payload);
        if (res?.success) {
          setEligibleCount(res.data.eligibleCount || 0);
          setRejectedSubscribers(res.data.rejectedSubscribers || []);
        }

        // Generate Preview
        const previewRes = await getCampaignPreview(activeCampaignId);
        if (previewRes?.success) {
          setPreviewData(previewRes.data);
        }
        setStep(5);
      } catch (err) {
        Swal.fire('Erreur', err.message || 'Erreur lors de la sélection des destinataires', 'error');
        return;
      } finally {
        setIsSubmitting(false);
      }
    } else if (step === 5) {
      setStep(6);
    } else if (step === 6) {
      setStep(7);
    }
  };

  const handlePrevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  // BAT test
  const handleSendBat = async () => {
    const rawList = batEmails.split(',').map((e) => e.trim()).filter(Boolean);
    if (rawList.length === 0 || rawList.length > 5) {
      Swal.fire('Validation', 'Veuillez saisir entre 1 et 5 adresses email de test séparées par une virgule.', 'warning');
      return;
    }

    setIsSubmitting(true);
    try {
      await sendTestBat(activeCampaignId, rawList);
      Swal.fire('BAT Expédié !', `Email de test envoyé avec succès à : ${rawList.join(', ')}`, 'success');
    } catch (err) {
      Swal.fire('Erreur BAT', err.message || 'Échec de l’envoi de test', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Final Send
  const handleFinalSend = async () => {
    if (!isAdmin) {
      Swal.fire('Accès Interdit', 'Seul un Administrateur peut déclencher l’envoi définitif d’une newsletter.', 'error');
      return;
    }

    if (confirmationTyped.trim() !== 'ENVOYER') {
      Swal.fire('Confirmation requise', 'Veuillez saisir exactement le mot "ENVOYER" en majuscules.', 'warning');
      return;
    }

    const confirmResult = await Swal.fire({
      title: 'DÉCLENCHER L’ENVOI DÉFINITIF ?',
      text: `Vous allez expédier cette campagne à ${eligibleCount} destinataire(s) éligible(s). Cette action fige le snapshot et est irréversible.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui, lancer l’envoi immédiat',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#0F2343',
    });

    if (confirmResult.isConfirmed) {
      setIsSubmitting(true);
      try {
        const res = await sendCampaign(activeCampaignId, idempotencyKey, confirmationTyped.trim());
        Swal.fire('Envoi Initié !', `La campagne a été expédiée avec succès. Statut : ${res.data?.campaign?.status}`, 'success');
        onCampaignUpdated?.();
        onClose();
      } catch (err) {
        Swal.fire('Erreur Envoi', err.message || 'Échec du déclenchement d’envoi', 'error');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="an-modal-backdrop animate-fade-in" onClick={onClose}>
      <div className="an-modal-card an-modal-xlarge" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="an-modal-header">
          <div className="an-modal-title">
            <Send size={22} className="text-primary mr-2" />
            <span>Assistant de Campagne Newsletter</span>
          </div>
          <button className="an-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Step Wizard Bar */}
        <div className="an-wizard-steps-bar">
          {[
            { num: 1, label: 'Informations' },
            { num: 2, label: 'Modèle & Intro' },
            { num: 3, label: 'Articles' },
            { num: 4, label: 'Destinataires' },
            { num: 5, label: 'Aperçu' },
            { num: 6, label: 'Test BAT' },
            { num: 7, label: 'Envoi Définitif' },
          ].map((st) => (
            <div
              key={st.num}
              className={`an-wizard-step-item ${step === st.num ? 'active' : ''} ${step > st.num ? 'completed' : ''}`}
            >
              <div className="an-wizard-step-circle">
                {step > st.num ? <Check size={14} /> : st.num}
              </div>
              <span className="an-wizard-step-label">{st.label}</span>
            </div>
          ))}
        </div>

        {/* Wizard Step Body */}
        <div className="an-modal-body an-modal-scrollable">
          {/* STEP 1: Basic Information */}
          {step === 1 && (
            <div className="an-wizard-step-content animate-fade-in">
              <h3 className="an-wizard-step-title">1. Informations Générales de la Campagne</h3>
              <div className="an-form-group">
                <label>Nom interne de la campagne <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="an-input"
                  placeholder="Ex: Newsletter Hebdomadaire Solaire - Février 2026 #04"
                  value={internalName}
                  onChange={(e) => setInternalName(e.target.value)}
                  required
                />
                <small className="text-muted">Visible uniquement par l'équipe dans le tableau de bord.</small>
              </div>

              <div className="an-form-group">
                <label>Objet de l'email (Subject) <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="an-input"
                  placeholder="Ex: ☀️ Nouvelle prime STEG & Actualités de la Transition Énergétique"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                />
              </div>

              <div className="an-form-group">
                <label>Pré-en-tête (Preheader text)</label>
                <input
                  type="text"
                  className="an-input"
                  placeholder="Ex: Découvrez les dernières décisions relatives au raccordement photovoltaïque..."
                  value={preheader}
                  onChange={(e) => setPreheader(e.target.value)}
                />
                <small className="text-muted">Aperçu affiché dans les boîtes de réception après l'objet.</small>
              </div>

              <div className="an-form-row">
                <div className="an-form-group flex-1">
                  <label>Nom de l'expéditeur</label>
                  <input
                    type="text"
                    className="an-input"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                  />
                </div>
                <div className="an-form-group flex-1">
                  <label>Email d'expédition (Relais SMTP dédié)</label>
                  <input
                    type="email"
                    className="an-input"
                    value={senderEmail}
                    onChange={(e) => setSenderEmail(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Template & Intro */}
          {step === 2 && (
            <div className="an-wizard-step-content animate-fade-in">
              <h3 className="an-wizard-step-title">2. Choix du Template & Introduction Éditoriale</h3>
              <div className="an-templates-grid">
                {TEMPLATES.map((tpl) => (
                  <div
                    key={tpl.key}
                    className={`an-template-card ${templateKey === tpl.key ? 'selected' : ''}`}
                    onClick={() => setTemplateKey(tpl.key)}
                  >
                    <div className="an-template-card-header">
                      <strong>{tpl.name}</strong>
                      {templateKey === tpl.key && <Check size={18} className="text-success" />}
                    </div>
                    <p className="an-template-card-desc">{tpl.desc}</p>
                  </div>
                ))}
              </div>

              <div className="an-form-group mt-4">
                <label>Introduction éditoriale / Message d'accueil</label>
                <TiptapEditor
                  content={editorialIntroHtml}
                  onChange={(html) => setEditorialIntroHtml(html)}
                  placeholder="Chers partenaires et abonnés, découvrez les actualités phares de cette semaine..."
                />
              </div>
            </div>
          )}

          {/* STEP 3: Featured Articles */}
          {step === 3 && (
            <div className="an-wizard-step-content animate-fade-in">
              <h3 className="an-wizard-step-title">3. Sélection des Articles à Intégrer</h3>
              <p className="text-muted">
                Sélectionnez les articles approuvés qui seront mis en avant dans la lettre d'information ({selectedArticleIds.length} sélectionné(s)).
              </p>

              {availableArticles.length === 0 ? (
                <div className="an-empty-state">
                  Aucun article approuvé disponible. Veuillez approuver des articles dans l'onglet Gestion des Articles.
                </div>
              ) : (
                <div className="an-articles-selection-list">
                  {availableArticles.map((art) => {
                    const isSelected = selectedArticleIds.includes(art._id);
                    return (
                      <div
                        key={art._id}
                        className={`an-article-select-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleArticleSelection(art._id)}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="an-checkbox mr-3"
                        />
                        {art.coverImage?.url && (
                          <img
                            src={art.coverImage.url}
                            alt=""
                            className="an-select-art-thumb mr-3"
                          />
                        )}
                        <div className="an-select-art-info">
                          <div className="an-select-art-title">{art.title}</div>
                          <div className="an-select-art-meta">
                            <span className="badge badge-light mr-2">{art.primaryCategoryId?.name}</span>
                            <span className="text-muted">{art.readingTimeMinutes || 2} min de lecture</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* STEP 4: Recipients (Mode A vs Mode B) */}
          {step === 4 && (
            <div className="an-wizard-step-content animate-fade-in">
              <h3 className="an-wizard-step-title">4. Sélection des Destinataires</h3>

              <div className="an-selection-modes-tabs">
                <button
                  type="button"
                  className={`an-mode-tab ${selectionMode === 'ALL_ELIGIBLE' ? 'active' : ''}`}
                  onClick={() => setSelectionMode('ALL_ELIGIBLE')}
                >
                  <Users size={18} className="mr-2" />
                  Mode A — Envoyer à tous les abonnés éligibles
                </button>
                <button
                  type="button"
                  className={`an-mode-tab ${selectionMode === 'MANUAL_SELECTION' ? 'active' : ''}`}
                  onClick={() => setSelectionMode('MANUAL_SELECTION')}
                >
                  <Layers size={18} className="mr-2" />
                  Mode B — Sélectionner manuellement les abonnés
                </button>
              </div>

              {selectionMode === 'ALL_ELIGIBLE' ? (
                <div className="an-mode-card-panel animate-fade-in">
                  <div className="an-alert-box info">
                    <Sparkles size={20} className="mr-2 text-primary" />
                    <div>
                      <strong>Mode Global Automatique :</strong> Le moteur SMTP ciblera automatiquement l'intégralité des abonnés disposant d'un consentement actif, d'un email confirmé, et non exclus des listes de diffusion.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="an-mode-card-panel animate-fade-in">
                  <div className="an-sub-selector-toolbar">
                    <input
                      type="text"
                      className="an-input"
                      placeholder="Rechercher par nom ou email..."
                      value={subscribersSearch}
                      onChange={(e) => {
                        setSubscribersSearch(e.target.value);
                        loadSubscribers(e.target.value, 1);
                      }}
                    />
                    <div className="an-selected-count-badge">
                      {selectedSubscriberIds.length} sélectionné(s) sur {subscribersTotal} abonnés
                    </div>
                  </div>

                  <div className="an-table-responsive mt-3">
                    <table className="an-table">
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}></th>
                          <th>Email</th>
                          <th>Nom complet</th>
                          <th>Statut</th>
                          <th>Consentement</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscribers.map((sub) => {
                          const isChecked = selectedSubscriberIds.includes(sub._id);
                          const isEligible =
                            ['SUBSCRIBED', 'ACTIVE'].includes(sub.status) &&
                            !sub.isExcluded &&
                            (sub.consent?.granted || sub.consentGiven);

                          return (
                            <tr
                              key={sub._id}
                              className={isChecked ? 'table-active' : ''}
                              onClick={() => toggleSubscriberSelection(sub._id)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => {}}
                                />
                              </td>
                              <td className="font-bold">{sub.email}</td>
                              <td>{sub.fullName || '-'}</td>
                              <td>
                                <span className={`an-badge an-badge-sub-${sub.status?.toLowerCase()}`}>
                                  {sub.status}
                                </span>
                              </td>
                              <td>
                                {isEligible ? (
                                  <span className="text-success font-semibold">✓ Éligible</span>
                                ) : (
                                  <span className="text-danger font-semibold">✗ Inéligible</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination for subscribers */}
                  <div className="an-pagination-row mt-2">
                    <button
                      type="button"
                      className="an-btn-secondary btn-sm"
                      disabled={subscribersPage <= 1}
                      onClick={() => loadSubscribers(subscribersSearch, subscribersPage - 1)}
                    >
                      Précédent
                    </button>
                    <span className="an-page-info">Page {subscribersPage}</span>
                    <button
                      type="button"
                      className="an-btn-secondary btn-sm"
                      disabled={subscribers.length < 10}
                      onClick={() => loadSubscribers(subscribersSearch, subscribersPage + 1)}
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 5: Preview */}
          {step === 5 && (
            <div className="an-wizard-step-content animate-fade-in">
              <div className="an-preview-header-bar">
                <h3 className="an-wizard-step-title mb-0">5. Prévisualisation de la Campagne</h3>
                <div className="an-device-toggles">
                  <button
                    type="button"
                    className={`an-device-btn ${previewDevice === 'desktop' ? 'active' : ''}`}
                    onClick={() => setPreviewDevice('desktop')}
                  >
                    <Monitor size={16} className="mr-1" /> Desktop
                  </button>
                  <button
                    type="button"
                    className={`an-device-btn ${previewDevice === 'mobile' ? 'active' : ''}`}
                    onClick={() => setPreviewDevice('mobile')}
                  >
                    <Smartphone size={16} className="mr-1" /> Mobile
                  </button>
                  <button
                    type="button"
                    className={`an-device-btn ${previewDevice === 'text' ? 'active' : ''}`}
                    onClick={() => setPreviewDevice('text')}
                  >
                    Texte Brut
                  </button>
                </div>
              </div>

              {/* Stats summary before sending */}
              <div className="an-eligibility-summary-box mt-3">
                <div className="an-stat-chip">
                  <span className="label">Destinataires validés :</span>
                  <span className="val text-success font-bold">{eligibleCount} abonnés éligibles</span>
                </div>
                {rejectedSubscribers.length > 0 && (
                  <div className="an-stat-chip">
                    <span className="label">Écartés par sécurité :</span>
                    <span className="val text-danger font-bold">
                      {rejectedSubscribers.length} destinataire(s) non éligible(s)
                    </span>
                  </div>
                )}
              </div>

              {/* Preview Rendering Frame */}
              <div className={`an-email-preview-frame ${previewDevice}`}>
                {previewDevice === 'text' ? (
                  <pre className="an-preview-text-box">
                    {previewData?.text || 'Aucune version texte disponible.'}
                  </pre>
                ) : (
                  <div
                    className="an-preview-html-box"
                    dangerouslySetInnerHTML={{
                      __html: previewData?.html || '<p>Génération de l’aperçu...</p>',
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {/* STEP 6: Test BAT */}
          {step === 6 && (
            <div className="an-wizard-step-content animate-fade-in">
              <h3 className="an-wizard-step-title">6. Envoi de Test (Bon à Tirer - BAT)</h3>
              <p className="text-muted">
                Avant d'expédier la campagne à vos abonnés, envoyez un Bon à Tirer (BAT) à votre équipe pour contrôler le rendu dans différents webmails (Gmail, Outlook, Apple Mail).
              </p>

              <div className="an-form-group">
                <label>Adresses email de test (1 à 5 adresses séparées par des virgules)</label>
                <input
                  type="text"
                  className="an-input"
                  placeholder="direction@sastraenergy.com, marketing@sastraenergy.com"
                  value={batEmails}
                  onChange={(e) => setBatEmails(e.target.value)}
                />
              </div>

              <button
                type="button"
                className="an-btn-primary"
                onClick={handleSendBat}
                disabled={isSubmitting || !batEmails.trim()}
              >
                <MailCheck size={16} />
                <span>{isSubmitting ? 'Envoi du test...' : 'Envoyer le Bon à Tirer (BAT)'}</span>
              </button>
            </div>
          )}

          {/* STEP 7: Final Send Triple Lock */}
          {step === 7 && (
            <div className="an-wizard-step-content animate-fade-in">
              <div className="an-send-lock-card">
                <div className="an-lock-icon-wrap">
                  <ShieldAlert size={42} className="text-danger" />
                </div>
                <h3 className="text-danger mt-2">Triple Verrou de Sécurité — Envoi Manuel Définitif</h3>
                <p className="an-send-lock-desc">
                  L'envoi définitif d'une newsletter est strictement réservé au rôle <strong>Administrateur</strong>. Cette action enregistrera le snapshot cryptographique des {eligibleCount} destinataires et déclenchera la file SMTP.
                </p>

                {!isAdmin && (
                  <div className="an-alert-box danger">
                    <AlertTriangle size={20} className="mr-2" />
                    <div>
                      <strong>Action non autorisée :</strong> Votre compte actuel n'a pas le rôle Administrateur. Seul un administrateur peut confirmer l'envoi définitif.
                    </div>
                  </div>
                )}

                <div className="an-send-lock-form mt-4">
                  <div className="an-form-group">
                    <label>
                      Pour confirmer l'envoi, veuillez saisir le mot <strong>ENVOYER</strong> en majuscules :
                    </label>
                    <input
                      type="text"
                      className="an-input an-input-lg text-center font-bold"
                      placeholder="ENVOYER"
                      value={confirmationTyped}
                      onChange={(e) => setConfirmationTyped(e.target.value)}
                      disabled={!isAdmin || isSubmitting}
                    />
                  </div>

                  <div className="an-idempotency-badge">
                    Clé d'idempotence anti-doublon : <code>{idempotencyKey}</code>
                  </div>

                  <button
                    type="button"
                    className="an-btn-danger an-btn-lg mt-4 w-100"
                    onClick={handleFinalSend}
                    disabled={!isAdmin || confirmationTyped.trim() !== 'ENVOYER' || isSubmitting}
                  >
                    <Send size={18} />
                    <span>
                      {isSubmitting
                        ? 'Envoi en cours...'
                        : `Déclencher l'envoi définitif (${eligibleCount} abonnés)`}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer Controls */}
        <div className="an-modal-footer">
          <button
            type="button"
            className="an-btn-secondary"
            onClick={step === 1 ? onClose : handlePrevStep}
            disabled={isSubmitting}
          >
            {step === 1 ? 'Annuler' : <><ChevronLeft size={16} /> Précédent</>}
          </button>

          {step < 7 && (
            <button
              type="button"
              className="an-btn-primary"
              onClick={handleNextStep}
              disabled={isSubmitting}
            >
              <span>{isSubmitting ? 'Traitement...' : 'Suivant'}</span>
              <ChevronRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

